import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  cn,
  DashboardCard,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  StatusBadge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@zafroi/ui-kit';
import {
  ArrowRight,
  ChevronDown,
  Globe,
  Mail,
  MessageSquare,
  Search,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSupportRequests } from '@/hooks/useEngageQueries';
import { useNewSinceAnchor } from '@/hooks/useNewSinceAnchor';
import { useTenant } from '@/services/tenant-context';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { NewSincePill } from '@/components/common/NewSincePill';
import {
  channelKindLabel,
  supportRequestStatusVariants,
} from '@/types/status-variants';
import { relativeTime } from '@/lib/format';
import type {
  ChannelKind,
  ConversationChannel,
  SupportRequest,
  SupportRequestPriority,
  SupportRequestStatus,
} from '@/types/engage';

/**
 * Status filter dimension. `open` is a *smart* aggregate matching the set of
 * statuses that need agent attention (pending + triaged + in_progress) — it's
 * the default on page open because agents come here to triage, not browse.
 * `all` shows everything regardless of status.
 */
type StatusFilter = SupportRequestStatus | 'open' | 'all';

const OPEN_STATUSES: SupportRequestStatus[] = [
  'pending',
  'triaged',
  'in_progress',
];

const STATUS_TABS: Array<{ id: StatusFilter; label: string }> = [
  { id: 'open', label: 'Open' },
  { id: 'pending', label: 'Pending' },
  { id: 'triaged', label: 'Triaged' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'closed', label: 'Closed' },
  { id: 'all', label: 'All' },
];

/**
 * Channel filter dimension. WhatsApp / SMS / Phone / Webchat are hidden until
 * those adapters ship — same approach as the Conversations inbox. Keep the
 * two lists in sync when more adapters go live.
 */
const CHANNEL_OPTIONS: Array<{ id: ChannelKind | 'all'; label: string }> = [
  { id: 'all', label: 'All channels' },
  { id: 'email', label: 'Email' },
  { id: 'portal', label: 'Portal' },
];

const PRIORITY_OPTIONS: Array<{
  id: SupportRequestPriority | 'all';
  label: string;
}> = [
  { id: 'all', label: 'All priorities' },
  { id: 'urgent', label: 'Urgent' },
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
];

// Smart-sort weights. Higher = sorted earlier within the list. Resolved and
// closed don't really need to be sorted by these — those views fall back to
// `createdAt desc` because they're for browsing, not triage.
const STATUS_WEIGHT: Record<SupportRequestStatus, number> = {
  pending: 4,
  triaged: 3,
  in_progress: 2,
  resolved: 1,
  closed: 0,
};

const PRIORITY_WEIGHT: Record<SupportRequestPriority, number> = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1,
};

// Color bar tone per status — drives the 4px leading edge on each row. Maps
// roughly to the status badge tone but resolved into Tailwind utility classes
// (the kit's badge handles tone → tint internally, but rows need a raw bg
// class on a sliver element).
const STATUS_BAR_CLASS: Record<SupportRequestStatus, string> = {
  pending: 'bg-warning',
  triaged: 'bg-muted-foreground/40',
  in_progress: 'bg-info',
  resolved: 'bg-success',
  closed: 'bg-muted-foreground/30',
};

// Tonal palette for avatar backgrounds — derived per-customer via a cheap
// deterministic hash so the same customer always gets the same colour. All
// classes reference the kit's tone tokens so they cooperate with dark mode.
const AVATAR_TONES = [
  'bg-primary/15 text-primary-700 dark:text-primary-200',
  'bg-info/15 text-info',
  'bg-success/15 text-success',
  'bg-warning/15 text-warning-foreground dark:text-warning',
  'bg-accent/15 text-accent-700 dark:text-accent-100',
  'bg-destructive/15 text-destructive',
  'bg-muted text-muted-foreground',
] as const;

// Channel icon for each kind we render. Email + Portal are the only shipped
// adapters today; other kinds fall back to MessageSquare so legacy rows in
// fixtures still render something sensible.
const CHANNEL_ICON: Record<string, LucideIcon> = {
  email: Mail,
  portal: Globe,
  whatsapp: MessageSquare,
  sms: MessageSquare,
  webchat: MessageSquare,
  phone: MessageSquare,
};

function matchesStatus(req: SupportRequest, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'open') return OPEN_STATUSES.includes(req.status);
  return req.status === filter;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// FNV-1a-ish hash — small, stable, no deps. Good enough for picking a tone.
function hashString(value: string): number {
  let h = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
}

function avatarToneFor(seed: string): string {
  return AVATAR_TONES[hashString(seed) % AVATAR_TONES.length];
}

export function SupportRequestsPage() {
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  // 30s polling is scoped to the page (not the hook) so Dashboard counts and
  // sidebar badges that also call `useSupportRequests()` keep their default
  // focus-only refetch — we don't want a background tab silently polling.
  const query = useSupportRequests({ refetchInterval: 30_000 });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('open');
  const [channel, setChannel] = useState<ChannelKind | 'all'>('all');
  const [priority, setPriority] = useState<SupportRequestPriority | 'all'>(
    'all',
  );

  // React Query keys are tenant-scoped, but the local filter UI state isn't —
  // reset to smart defaults whenever the operator switches tenants so they
  // don't carry an aggressive filter from one workspace into another.
  useEffect(() => {
    setSearch('');
    setStatus('open');
    setChannel('all');
    setPriority('all');
  }, [currentTenant.id]);

  const all = query.data ?? [];
  const { newCount, isNew, acknowledge } = useNewSinceAnchor(all);

  // Counts are derived from the *unfiltered* list so the tab numbers don't
  // shift around as other filters change. Standard pattern (Linear / Gmail /
  // Intercom) — surface what's behind each option without coupling to the
  // rest of the current selection.
  const statusCounts = useMemo<Record<StatusFilter, number>>(() => {
    const out: Record<StatusFilter, number> = {
      all: all.length,
      open: 0,
      pending: 0,
      triaged: 0,
      in_progress: 0,
      resolved: 0,
      closed: 0,
    };
    for (const req of all) {
      out[req.status] += 1;
      if (OPEN_STATUSES.includes(req.status)) out.open += 1;
    }
    return out;
  }, [all]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = all.filter((req) => {
      if (!matchesStatus(req, status)) return false;
      if (channel !== 'all' && req.channel !== channel) return false;
      if (priority !== 'all' && req.priority !== priority) return false;
      if (!needle) return true;
      return (
        req.subject.toLowerCase().includes(needle) ||
        req.customerName.toLowerCase().includes(needle)
      );
    });

    // Smart triage sort:
    //   1. Status weight (pending > triaged > in_progress > resolved > closed)
    //   2. Priority weight (urgent > high > medium > low)
    //   3. Oldest first within priority — the most-waiting request rises
    //
    // For `resolved`, `closed`, and `all` views we fall back to createdAt desc
    // because those views are for browsing, not action — newest-first reads
    // more naturally there.
    const browseMode = status === 'resolved' || status === 'closed' || status === 'all';
    return [...rows].sort((a, b) => {
      if (browseMode) {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      const sw = STATUS_WEIGHT[b.status] - STATUS_WEIGHT[a.status];
      if (sw !== 0) return sw;
      const pw = PRIORITY_WEIGHT[b.priority] - PRIORITY_WEIGHT[a.priority];
      if (pw !== 0) return pw;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }, [all, search, status, channel, priority]);

  const hasNoData = all.length === 0;
  const activeSecondaryFilters =
    (channel !== 'all' ? 1 : 0) +
    (priority !== 'all' ? 1 : 0) +
    (search.trim().length > 0 ? 1 : 0);
  const hasActiveSecondary = activeSecondaryFilters > 0;
  // The status tab isn't a "filter chip" — it's the primary axis. But if the
  // operator is off the default tab AND has nothing in this view, we should
  // still offer them a way back.
  const isFilteredAway = status !== 'open' || hasActiveSecondary;

  const clearSecondary = () => {
    setSearch('');
    setChannel('all');
    setPriority('all');
  };

  const clearAll = () => {
    clearSecondary();
    setStatus('open');
  };

  const channelLabel = (id: ChannelKind | 'all') =>
    CHANNEL_OPTIONS.find((opt) => opt.id === id)?.label ?? id;
  const priorityLabel = (id: SupportRequestPriority | 'all') =>
    PRIORITY_OPTIONS.find((opt) => opt.id === id)?.label ?? id;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support"
        description="Requests from your customers, on every channel. Open one to read the conversation, reply, and resolve."
      />

      <DashboardCard flushBody>
        <NewSincePill
          count={newCount}
          onAcknowledge={acknowledge}
          noun={{ singular: 'request', plural: 'requests' }}
        />

        {/* Sticky filter region — segmented tabs + secondary filter row + */}
        {/* active-chip strip. `z-[5]` puts it under NewSincePill (z-10). */}
        <div className="sticky top-0 z-[5] border-b border-border bg-card">
          <SegmentedTabs
            value={status}
            onChange={setStatus}
            counts={statusCounts}
          />
          <div className="flex flex-col gap-2 px-5 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search subjects or customers…"
                className="h-8 pl-8"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <FilterDropdown
                label="Channel"
                value={channel}
                valueLabel={channelLabel(channel)}
                options={CHANNEL_OPTIONS}
                onChange={setChannel}
              />
              <FilterDropdown
                label="Priority"
                value={priority}
                valueLabel={priorityLabel(priority)}
                options={PRIORITY_OPTIONS}
                onChange={setPriority}
              />
            </div>
          </div>
          {hasActiveSecondary && (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-border/60 bg-muted/30 px-5 py-2">
              {search.trim() && (
                <ActiveChip
                  label={`Search: ${search.trim()}`}
                  onClear={() => setSearch('')}
                />
              )}
              {channel !== 'all' && (
                <ActiveChip
                  label={`Channel: ${channelLabel(channel)}`}
                  onClear={() => setChannel('all')}
                />
              )}
              {priority !== 'all' && (
                <ActiveChip
                  label={`Priority: ${priorityLabel(priority)}`}
                  onClear={() => setPriority('all')}
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSecondary}
                className="h-6 px-2 text-xs text-muted-foreground"
              >
                Clear all
              </Button>
            </div>
          )}
        </div>

        <QueryStateGuard
          data={filtered}
          isLoading={query.isLoading}
          isError={query.isError}
          emptyTitle={
            hasNoData ? 'No requests yet' : 'Nothing matches these filters'
          }
          emptyDescription={
            hasNoData
              ? 'When customers email or chat with your bot, requests will land here. Connect a channel to get started.'
              : 'Try the Open tab, or clear filters to widen the view.'
          }
          emptyAction={
            hasNoData ? (
              <Button size="sm" onClick={() => navigate('/channels')}>
                Connect a channel
              </Button>
            ) : isFilteredAway ? (
              <Button variant="outline" size="sm" onClick={clearAll}>
                <X className="h-3.5 w-3.5" />
                Clear filters
              </Button>
            ) : undefined
          }
        >
          {(rows) => (
            <ul className="divide-y divide-border">
              {rows.map((req) => (
                <SupportRequestRow
                  key={req.id}
                  request={req}
                  isNew={isNew(req)}
                  onOpen={() => navigate(`/support/${req.id}`)}
                />
              ))}
            </ul>
          )}
        </QueryStateGuard>
      </DashboardCard>
    </div>
  );
}

// ─── Segmented status tabs ──────────────────────────────────────────────────

interface SegmentedTabsProps {
  value: StatusFilter;
  onChange: (next: StatusFilter) => void;
  counts: Record<StatusFilter, number>;
}

/**
 * Underline-style segmented control. We went with underline over soft-filled
 * because the row content already carries a lot of tinted surface area (status
 * color bar, status badge, priority dot) — a filled-pill nav on top of that
 * starts to compete. Underline is the lighter touch and reads cleaner.
 *
 * Tabs with a count of 0 are rendered disabled — the option stays visible so
 * the operator can see it exists, but it can't be clicked. The currently
 * selected tab is never disabled (otherwise you'd get stuck on an empty view).
 */
function SegmentedTabs({ value, onChange, counts }: SegmentedTabsProps) {
  return (
    <div
      role="tablist"
      aria-label="Filter requests by status"
      className="flex items-end gap-0.5 overflow-x-auto px-5 pt-3"
    >
      {STATUS_TABS.map((tab) => {
        const count = counts[tab.id] ?? 0;
        const active = value === tab.id;
        const disabled = !active && count === 0;
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={active}
            disabled={disabled}
            onClick={() => onChange(tab.id)}
            className={cn(
              'group relative flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              active
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
              disabled &&
                'cursor-not-allowed opacity-50 hover:text-muted-foreground',
            )}
          >
            <span>{tab.label}</span>
            <span
              className={cn(
                'tnum text-xs',
                active ? 'text-muted-foreground' : 'text-muted-foreground/70',
              )}
            >
              ({count})
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Secondary filter dropdown ──────────────────────────────────────────────

interface FilterDropdownProps<T extends string> {
  label: string;
  value: T;
  valueLabel: string;
  options: ReadonlyArray<{ id: T; label: string }>;
  onChange: (next: T) => void;
}

function FilterDropdown<T extends string>({
  label,
  value,
  valueLabel,
  options,
  onChange,
}: FilterDropdownProps<T>) {
  const isDefault = value === ('all' as T);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={isDefault ? 'outline' : 'soft'}
          size="sm"
          className="h-8"
        >
          <span className="text-muted-foreground">{label}:</span>
          <span className="font-medium">{valueLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((opt) => {
          const selected = opt.id === value;
          return (
            <DropdownMenuItem
              key={opt.id}
              onSelect={() => onChange(opt.id)}
              className={cn(
                'text-sm',
                selected && 'bg-muted font-medium text-foreground',
              )}
            >
              {opt.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Active filter chip ─────────────────────────────────────────────────────

interface ActiveChipProps {
  label: string;
  onClear: () => void;
}

function ActiveChip({ label, onClear }: ActiveChipProps) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-xs text-foreground">
      <span className="text-muted-foreground">{label}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear ${label}`}
        className="-mr-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

// ─── Row component ──────────────────────────────────────────────────────────

interface SupportRequestRowProps {
  request: SupportRequest;
  isNew: boolean;
  onOpen: () => void;
}

/**
 * Rich row: status colour bar → customer avatar → priority dot + subject +
 * customer line → channel icon → status badge → relative time → hover arrow.
 *
 * The leading 4px bar is colour-coded by status by default; for requests
 * that arrived since the page was opened (`isNew`) we swap it for an info
 * tone instead of stacking — stacking two bars at 4px wide reads as visual
 * clutter, not signal.
 */
function SupportRequestRow({ request, isNew, onOpen }: SupportRequestRowProps) {
  const channelKey = request.channel as ConversationChannel;
  const ChannelIcon = CHANNEL_ICON[channelKey] ?? MessageSquare;
  const channelName = channelKindLabel[channelKey] ?? channelKey;
  const showPriorityDot =
    request.priority === 'urgent' || request.priority === 'high';
  const priorityDotClass =
    request.priority === 'urgent' ? 'bg-destructive' : 'bg-warning';
  const barClass = isNew ? 'bg-info' : STATUS_BAR_CLASS[request.status];

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          'group relative flex w-full items-center gap-3 py-3 pl-4 pr-4 text-left transition-colors hover:bg-muted/40 focus:bg-muted/40 focus:outline-none',
        )}
      >
        {/* Status colour bar — 4px leading edge. */}
        <span
          aria-hidden
          className={cn(
            'absolute left-0 top-0 h-full w-1',
            barClass,
            isNew && 'shadow-[0_0_0_1px] shadow-info/20',
          )}
        />

        {/* Customer avatar with tonal background. */}
        <span
          aria-hidden
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
            avatarToneFor(request.customerId || request.customerName),
          )}
        >
          {initialsOf(request.customerName)}
        </span>

        {/* Subject + customer line. */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {showPriorityDot && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    aria-label={`${request.priority} priority`}
                    className={cn(
                      'h-1.5 w-1.5 shrink-0 rounded-full',
                      priorityDotClass,
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  {request.priority === 'urgent' ? 'Urgent' : 'High'} priority
                </TooltipContent>
              </Tooltip>
            )}
            <span className="truncate text-sm font-medium text-foreground">
              {request.subject}
            </span>
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {request.customerName}
          </div>
        </div>

        {/* Channel icon — tooltip carries the name. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              aria-label={`Channel: ${channelName}`}
              className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground sm:inline-flex"
            >
              <ChannelIcon className="h-3.5 w-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent>{channelName}</TooltipContent>
        </Tooltip>

        {/* Status badge — smaller/tighter than the column-based version. */}
        <StatusBadge
          status={request.status}
          variants={supportRequestStatusVariants}
          className="hidden sm:inline-flex"
        />

        {/* Relative time — right-aligned, tabular numerals. */}
        <span className="tnum w-16 shrink-0 text-right text-xs text-muted-foreground">
          {relativeTime(request.createdAt)}
        </span>

        {/* Hover affordance — small arrow appears on the far right. */}
        <ArrowRight
          aria-hidden
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100"
        />
      </button>
    </li>
  );
}
